import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  Paper,
  Typography,
  Tabs,
  Tab,
  Drawer,
  IconButton,
  Fab,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  Stack,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Preview as PreviewIcon,
  Code as CodeIcon,
  AutoAwesome as AutoAwesomeIcon,
  Save as SaveIcon,
  FolderOpen as LoadIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  FileCopy as DuplicateIcon, // Fixed import
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { StyledEngineProvider } from '@mui/material/styles';

// Components
import FormBuilderHeader from '../components/FormBuilder/FormBuilderHeader';
import FormBuilderAiChatPanel from '../components/FormBuilder/FormBuilderAiChatPanel';
import FormEditor from '../components/FormEditor';
import JSONEditor from '../components/JSONEditor';
import FormRuntime from '../components/FormRuntime';
import EnhancedWidgetList from '../components/EnhancedWidgetList';
import ModalFormEditor from '../components/ModalFormEditor';
import {
  buildFormPayloadContract,
  generateDebugPayloadFromFormFields,
  validateFormDesignContract,
} from '../utils/formDesignToStudioV2Fields';

// Hooks
import { useFormBuilder } from '../hooks/useFormBuilder';

function FormBuilder({ darkMode: initialDarkMode, embedded = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Use custom hook for form builder logic
  const {
    darkMode,
    setDarkMode,
    formElements,
    setFormElements,
    selectedElement,
    setSelectedElement,
    undoStack,
    redoStack,
    handleUndo,
    handleRedo,
    moveFormElement,
  } = useFormBuilder(initialDarkMode);

  // Local state for UI
  const [selectedTab, setSelectedTab] = useState(0);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(true);
  const [previewMode, setPreviewMode] = useState('desktop');

  // Dialog states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Enhanced state for better UX
  const [searchQuery, setSearchQuery] = useState('');
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [savedForms, setSavedForms] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [publishing, setPublishing] = useState(false);
  const [jsonCode, setJsonCode] = useState('{}');

  // Responsive drawer for mobile
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = () => setDrawerOpen((open) => !open);

  /** Mongo id when a design was loaded from the server (optional context for AI). */
  const [loadedFormDesignId, setLoadedFormDesignId] = useState(null);

  const formPayloadContract = useMemo(() => buildFormPayloadContract(formElements, {
    id: loadedFormDesignId || 'draft_form',
    name: formName || 'Draft form',
  }), [formElements, formName, loadedFormDesignId]);
  const formContractDiagnostics = useMemo(() => validateFormDesignContract(formElements), [formElements]);
  const formDebugPayload = useMemo(
    () => generateDebugPayloadFromFormFields(formPayloadContract.fields),
    [formPayloadContract.fields],
  );

  const [formSettings, setFormSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('formBuilder_settings');
      return saved ? JSON.parse(saved) : {
        title: '',
        description: '',
        theme: 'default',
        submitText: 'Submit',
        resetText: 'Reset',
        showProgress: false,
        allowDrafts: true,
        emailNotifications: false,
        redirectAfterSubmit: '',
        formWidth: 'medium',
        buttonStyle: 'contained',
      };
    } catch (error) {
      return {
        title: '',
        description: '',
        theme: 'default',
        submitText: 'Submit',
        resetText: 'Reset',
        showProgress: false,
        allowDrafts: true,
        emailNotifications: false,
        redirectAfterSubmit: '',
        formWidth: 'medium',
        buttonStyle: 'contained',
      };
    }
  });

  // Save formElements to localStorage on change (autosave)
  useEffect(() => {
    try {
      localStorage.setItem('formBuilder_elements', JSON.stringify(formElements));
    } catch (error) {
      console.warn('Failed to save form elements to localStorage:', error);
    }
  }, [formElements]);

  // Save formSettings to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem('formBuilder_settings', JSON.stringify(formSettings));
    } catch (error) {
      console.warn('Failed to save form settings to localStorage:', error);
    }
  }, [formSettings]);

  // Force a re-render and trigger the autosave effect on mount
  useEffect(() => {
    // This effect runs after component mounts and ensures the autosave effect is triggered
    if (formElements.length === 0) {
      // Trigger a state update to ensure the autosave effect runs
      setFormElements([]);
    }
  }, []); // Empty dependency array means this runs once on mount

  // Update jsonCode whenever formElements OR formSettings changes (keep JSON in sync)
  useEffect(() => {
    // Generate schema and uischema from formElements (same logic as in FormEditor/FormRuntime)
    const getJsonSchemaType = (widgetType) => {
      switch (widgetType) {
        case 'number':
          return 'number';
        case 'checkbox':
          return 'boolean';
        case 'date':
        case 'datetime':
        case 'text':
        case 'textarea':
        case 'listbox':
        case 'combobox':
        case 'radiobuttons':
          return 'string';
        default:
          return 'string';
      }
    };

    const gridColumnKeys = formElements
      .filter((el) => el.type === 'grid')
      .flatMap((el) => (el.columns || []).map((col) => col.key));
    const groupboxFieldIds = formElements
      .filter((el) => el.type === 'groupbox')
      .flatMap((el) => el.fields || []);

    const schema = {
      type: 'object',
      title: formSettings.title || 'Form',
      description: formSettings.description || '',
      properties: formElements.reduce((acc, el) => {
        if (el.type === 'grid') {
          acc[el.id] = {
            type: 'array',
            title: el.label,
            items: {
              type: 'object',
              properties: Array.isArray(el.columns)
                ? el.columns.reduce((cols, col) => {
                  cols[col.key || col.id || `col_${Math.random()}`] = {
                    type: getJsonSchemaType(col.type),
                    title: col.label || col.key || col.type,
                  };
                  return cols;
                }, {})
                : {},
            },
          };
          return acc;
        }
        if (el.type === 'groupbox') {
          acc[el.id] = {
            type: 'object',
            title: el.label,
            properties: (el.fields || []).reduce((fieldsAcc, fieldId) => {
              const field = formElements.find((f) => f.id === fieldId);
              if (!field) return fieldsAcc;
              const type = getJsonSchemaType(field.type);
              const fieldSchema = { type, title: field.label };
              if (field.type === 'date') fieldSchema.format = 'date';
              if (field.type === 'datetime') fieldSchema.format = 'date-time';
              if (field.options && field.options.length) fieldSchema.enum = field.options;
              if (field.minLength) fieldSchema.minLength = field.minLength;
              if (field.maxLength) fieldSchema.maxLength = field.maxLength;
              if (field.pattern) fieldSchema.pattern = field.pattern;
              if (field.minimum) fieldSchema.minimum = field.minimum;
              if (field.maximum) fieldSchema.maximum = field.maximum;
              if (field.multipleOf) fieldSchema.multipleOf = field.multipleOf;
              if (field.required) fieldSchema.required = true;
              fieldsAcc[field.id] = fieldSchema;
              return fieldsAcc;
            }, {}),
          };
          return acc;
        }
        // Exclude fields that are columns in any grid or in any groupbox
        if (
          gridColumnKeys.includes(el.id)
          || gridColumnKeys.includes(el.key)
          || groupboxFieldIds.includes(el.id)
        ) return acc;
        const type = getJsonSchemaType(el.type);
        const elementSchema = { type, title: el.label };
        if (el.type === 'date') elementSchema.format = 'date';
        if (el.type === 'datetime') elementSchema.format = 'date-time';
        if (el.options && el.options.length) elementSchema.enum = el.options;
        if (el.minLength) elementSchema.minLength = el.minLength;
        if (el.maxLength) elementSchema.maxLength = el.maxLength;
        if (el.pattern) elementSchema.pattern = el.pattern;
        if (el.minimum) elementSchema.minimum = el.minimum;
        if (el.maximum) elementSchema.maximum = el.maximum;
        if (el.multipleOf) elementSchema.multipleOf = el.multipleOf;
        if (el.required) elementSchema.required = true;
        acc[el.id] = elementSchema;
        return acc;
      }, {}),
    };

    const uischema = {
      type: 'VerticalLayout',
      elements: formElements
        .filter((el) => {
          if (el.type === 'grid' || el.type === 'groupbox') return true;
          if (
            gridColumnKeys.includes(el.id)
            || gridColumnKeys.includes(el.key)
            || groupboxFieldIds.includes(el.id)
          ) return false;
          return true;
        })
        .map((el) => {
          const options = {
            format: el.type === 'checkbox' ? 'checkbox' : el.type,
            enum: el.options,
            minLength: el.minLength,
            maxLength: el.maxLength,
            pattern: el.pattern,
            minimum: el.minimum,
            maximum: el.maximum,
            multipleOf: el.multipleOf,
          };
          if (el.type === 'date') options.format = 'date';
          if (el.type === 'datetime') options.format = 'date-time';
          if (el.type === 'textarea') {
            options.format = 'textarea';
            options.multi = true;
            options.rows = el.rows || 4;
          }
          if (el.type === 'grid') {
            return {
              type: 'Control',
              scope: `#/properties/${el.id}`,
              options: { table: true },
            };
          }
          if (el.type === 'groupbox') {
            return {
              type: 'Group',
              label: el.label,
              elements: (el.fields || []).map((fieldId) => ({
                type: 'Control',
                scope: `#/properties/${el.id}/properties/${fieldId}`,
              })),
            };
          }
          return {
            type: 'Control',
            scope: `#/properties/${el.id}`,
            options,
          };
        }),
    };

    // Include form settings in the JSON output
    const fullJsonSchema = {
      schema,
      uischema,
      formSettings: {
        title: formSettings.title,
        description: formSettings.description,
        theme: formSettings.theme,
        submitText: formSettings.submitText,
        resetText: formSettings.resetText,
        showProgress: formSettings.showProgress,
        allowDrafts: formSettings.allowDrafts,
        emailNotifications: formSettings.emailNotifications,
        redirectAfterSubmit: formSettings.redirectAfterSubmit,
        formWidth: formSettings.formWidth,
        buttonStyle: formSettings.buttonStyle,
      },
    };

    setJsonCode(JSON.stringify(fullJsonSchema, null, 2));
  }, [formElements, formSettings]);

  // Modal handlers
  const handleOpenModal = (element) => {
    setSelectedElement(element);
    setIsModalOpen(true);
    setShowPropertiesPanel(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedElement(null);
  };

  const handleSaveChanges = (updatedElement) => {
    setFormElements((prevElements) => prevElements.map((el) => (el.id === updatedElement.id ? updatedElement : el)));
    handleCloseModal();
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleToggleTheme = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    // Fix: Only use localStorage for theme
    localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');

    // Dispatch event to notify App component
    window.dispatchEvent(new CustomEvent('themeChanged', {
      detail: { darkMode: newDarkMode },
    }));
  };

  // Load saved forms from backend on component mount
  useEffect(() => {
    loadSavedForms();
  }, []);

  // Listen for openFormLoader event from global search
  useEffect(() => {
    const handleOpenFormLoader = () => setLoadDialogOpen(true);
    window.addEventListener('openFormLoader', handleOpenFormLoader);
    return () => window.removeEventListener('openFormLoader', handleOpenFormLoader);
  }, []);

  // Load saved forms from backend
  const loadSavedForms = async () => {
    try {
      const response = await fetch('/api/v1/forms/designs', {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSavedForms(data.forms || []);
      } else {
        console.error('Failed to load saved forms');
      }
    } catch (error) {
      console.error('Error loading saved forms:', error);
    }
  };

  // Save form design to backend - CHECK FOR EXISTING FORMS FIRST
  const saveFormDesign = async () => {
    if (!formName.trim()) {
      setSnackbar({ open: true, message: 'Please enter a form name', severity: 'error' });
      return;
    }

    try {
      // First check if user already has a form design
      const checkResponse = await fetch('/api/v1/forms/designs', {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      let existingFormId = null;
      if (checkResponse.ok) {
        const data = await checkResponse.json();
        const existingForms = data.forms || [];
        if (existingForms.length > 0) {
          existingFormId = existingForms[0]._id; // Use the first existing form ID
        }
      }

      // Map frontend field types to backend valid types
      const mapFieldType = (type) => {
        switch (type) {
          case 'email':
          case 'password':
          case 'url':
            return 'text'; // Map email, password, url to text
          case 'textarea':
            return 'textarea';
          case 'number':
            return 'number';
          case 'checkbox':
            return 'checkbox';
          case 'date':
            return 'date';
          case 'datetime':
            return 'datetime';
          case 'combobox':
            return 'combobox';
          case 'listbox':
            return 'listbox';
          case 'radiobuttons':
            return 'radiobuttons';
          case 'grid':
            return 'grid';
          case 'groupbox':
            return 'groupbox';
          default:
            return 'text'; // Default fallback
        }
      };

      // Transform formElements to use valid backend types
      const transformedFormElements = formElements.map((element) => {
        // Clean up grid columns: remove extra/irrelevant properties from columns
        let { columns } = element;
        if (element.type === 'grid' && Array.isArray(element.columns)) {
          columns = element.columns.map((col) => ({
            id: col.id || col.key || (`${col.type}_${Date.now()}`),
            key: col.key || col.id || (`${col.type}_${Date.now()}`),
            label: col.label || col.key || col.type,
            type: col.type || 'string',
            required: !!col.required,
            placeholder: col.placeholder || '',
            disabled: !!col.disabled,
            readonly: !!col.readonly,
            minLength: col.minLength,
            maxLength: col.maxLength,
            pattern: col.pattern,
            minimum: col.minimum,
            maximum: col.maximum,
            multipleOf: col.multipleOf,
            options: col.options || [],
            enum: col.enum || [],
            description: col.description || '',
            helpText: col.helpText || '',
            errorMessage: col.errorMessage || '',
            className: col.className || '',
            style: col.style || {},
          }));
        }
        // Remove columns from non-grid fields
        if (element.type !== 'grid') {
          columns = undefined;
        }
        return {
          ...element,
          type: mapFieldType(element.type),
          originalType: element.type,
          inputType: element.type === 'email' ? 'email'
            : element.type === 'password' ? 'password'
              : element.type === 'url' ? 'url'
                : element.type === 'number' ? 'number' : 'text',
          columns,
        };
      });

      // --- DEBUG: Log all formElements before sending ---
      // Remove/comment this in production
      // console.log('All formElements before transform:', formElements);
      // console.log('Transformed formElements:', transformedFormElements);

      // Prepare the payload
      const payload = {
        name: formName,
        description: formDescription,
        formElements: transformedFormElements,
        formSettings,
        formConfig: {
          version: '1.0.0',
          createdAt: new Date().toISOString(),
        },
        payloadContract: formPayloadContract,
        debugPayload: formDebugPayload,
        status: 'draft',
        tags: [],
        category: 'general',
      };

      // console.log('Saving form with transformed payload:', payload);

      let response;
      let method = 'POST';
      let url = '/api/v1/forms/design';

      // If user already has a form, update it instead of creating new one
      if (existingFormId) {
        method = 'PUT';
        url = `/api/v1/forms/design/${existingFormId}`;
        setSnackbar({
          open: true,
          message: 'Updating existing form design...',
          severity: 'info',
        });
      }

      response = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      // console.log(`${method} response:`, response.status, responseText);

      if (response.ok) {
        const data = JSON.parse(responseText);
        const successMessage = existingFormId ? 'Form design updated successfully!' : 'Form design saved successfully!';
        setSnackbar({ open: true, message: successMessage, severity: 'success' });
        setSaveDialogOpen(false);
        // DON'T clear form name and description - keep them for future saves
        // setFormName('');
        // setFormDescription('');
        loadSavedForms();
      } else {
        let errorMessage = `Failed to ${existingFormId ? 'update' : 'save'} form design`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || errorMessage;

          // Handle specific duplicate key error
          if (errorMessage.includes('duplicate key error') || errorMessage.includes('E11000')) {
            errorMessage = 'Unable to save due to database constraints. This appears to be a backend configuration issue. Please contact support.';
          }
        } catch (e) {
          errorMessage = `Server error: ${response.status} - ${responseText}`;
        }
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        console.error('Save error:', errorMessage);
      }
    } catch (error) {
      console.error('Error saving form design:', error);
      setSnackbar({ open: true, message: `Error saving form design: ${error.message}`, severity: 'error' });
    }
  };

  // Publish form - USE SAME LOGIC
  const publishForm = async () => {
    if (!formName.trim()) {
      setSnackbar({ open: true, message: 'Please save the form first before publishing', severity: 'error' });
      return;
    }

    setPublishing(true);
    try {
      // Check for existing forms first
      const checkResponse = await fetch('/api/v1/forms/designs', {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      let existingFormId = null;
      if (checkResponse.ok) {
        const data = await checkResponse.json();
        const existingForms = data.forms || [];
        if (existingForms.length > 0) {
          existingFormId = existingForms[0]._id;
        }
      }

      // Same type mapping function
      const mapFieldType = (type) => {
        switch (type) {
          case 'email':
          case 'password':
          case 'url':
            return 'text';
          default:
            return type;
        }
      };

      // Transform formElements
      const transformedFormElements = formElements.map((element) => ({
        ...element,
        type: mapFieldType(element.type),
        originalType: element.type,
        inputType: element.type === 'email' ? 'email'
          : element.type === 'password' ? 'password'
            : element.type === 'url' ? 'url'
              : element.type === 'number' ? 'number' : 'text',
        // --- ADD: persist columns for grid fields ---
        columns: element.type === 'grid' && Array.isArray(element.columns)
          ? element.columns.map((col) => ({
            ...col,
            // Ensure each column has key, label, type, and id
            key: col.key || col.id || (`${col.type}_${Date.now()}`),
            label: col.label || col.key || col.type,
            type: col.type || 'string',
            id: col.id || col.key || (`${col.type}_${Date.now()}`),
          }))
          : element.columns,
      }));

      const savePayload = {
        name: formName,
        description: formDescription,
        formElements: transformedFormElements,
        formSettings,
        formConfig: {
          version: '1.0.0',
          createdAt: new Date().toISOString(),
        },
        payloadContract: formPayloadContract,
        debugPayload: formDebugPayload,
        status: 'draft',
        tags: [],
        category: 'general',
      };

      let saveResponse;

      if (existingFormId) {
        // Update existing form
        saveResponse = await fetch(`/api/v1/forms/design/${existingFormId}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(savePayload),
        });
      } else {
        // Create new form
        saveResponse = await fetch('/api/v1/forms/design', {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(savePayload),
        });
      }

      if (saveResponse.ok) {
        const saveData = await saveResponse.json();
        const formId = existingFormId || saveData.formDesign?._id || saveData._id;

        // Now publish using the existing route
        const publishResponse = await fetch(`/api/v1/forms/design/${formId}/publish`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: 'published',
            formSettings,
          }),
        });

        if (publishResponse.ok) {
          setSnackbar({ open: true, message: 'Form published successfully!', severity: 'success' });
          loadSavedForms();
        } else {
          throw new Error('Failed to publish form');
        }
      } else {
        const errorText = await saveResponse.text();
        let errorMessage = 'Failed to save form before publishing';

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;

          if (errorMessage.includes('duplicate key error') || errorMessage.includes('E11000')) {
            errorMessage = 'Unable to save due to database constraints. Please contact support.';
          }
        } catch (e) {
          errorMessage = `Save error: ${saveResponse.status} - ${errorText}`;
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error publishing form:', error);
      setSnackbar({ open: true, message: `Failed to publish form: ${error.message}`, severity: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  // Load form design - RESTORE ORIGINAL TYPES AND SET FORM NAME
  const loadFormDesign = async (formId) => {
    try {
      const response = await fetch(`/api/v1/forms/design/${formId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const fd = data.formDesign || data;
        // Restore original types when loading
        const restoredFormElements = (fd.formElements || []).map((element) => ({
          ...element,
          type: element.originalType || element.type, // Use originalType if available
        }));
        setFormElements(restoredFormElements);

        // Set the form name and description from loaded form
        setFormName(fd.name || data.name || '');
        setFormDescription(fd.description || data.description || '');
        setLoadedFormDesignId(fd._id || formId);

        setSnackbar({ open: true, message: 'Form design loaded successfully!', severity: 'success' });
        setLoadDialogOpen(false);
      } else {
        const errorData = await response.json();
        setSnackbar({ open: true, message: errorData.message || 'Failed to load form design', severity: 'error' });
      }
    } catch (error) {
      console.error('Error loading form design:', error);
      setSnackbar({ open: true, message: 'Error loading form design', severity: 'error' });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const formId = params.get('formId') || params.get('load');
    if (formId) {
      void loadFormDesign(formId);
    }
  }, []);

  // Delete form design
  const deleteFormDesign = async (formId) => {
    try {
      const response = await fetch(`/api/v1/forms/design/${formId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Form design deleted successfully!', severity: 'success' });
        loadSavedForms(); // Refresh the list
      } else {
        const errorData = await response.json();
        setSnackbar({ open: true, message: errorData.message || 'Failed to delete form design', severity: 'error' });
      }
    } catch (error) {
      console.error('Error deleting form design:', error);
      setSnackbar({ open: true, message: 'Error deleting form design', severity: 'error' });
    }
  };

  // Export form as JSON file
  const exportFormAsJSON = () => {
    const exportData = {
      name: formName || 'Untitled Form',
      description: formDescription,
      formElements,
      jsonSchema: jsonCode,
      payloadContract: formPayloadContract,
      debugPayload: formDebugPayload,
      exportedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formName || 'form-design'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSnackbar({ open: true, message: 'Form exported as JSON file!', severity: 'success' });
  };

  // Duplicate form - SET FORM NAME WHEN DUPLICATING
  const duplicateForm = (formId) => {
    const formToDuplicate = savedForms.find((f) => f._id === formId);
    if (formToDuplicate) {
      setFormElements(formToDuplicate.formElements || []);
      setFormName(`${formToDuplicate.name} (Copy)`);
      setFormDescription(formToDuplicate.description || '');
      setLoadedFormDesignId(null);
      setSnackbar({ open: true, message: 'Form duplicated successfully!', severity: 'success' });
    }
  };

  // Import form from JSON file
  const importFormFromJSON = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        if (importedData.formElements) {
          setFormElements(importedData.formElements);
          setFormName(importedData.name || 'Imported Form');
          setFormDescription(importedData.description || '');
          setLoadedFormDesignId(null);
          setSnackbar({ open: true, message: 'Form imported successfully!', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: 'Invalid form file format', severity: 'error' });
        }
      } catch (error) {
        console.error('Error importing form:', error);
        setSnackbar({ open: true, message: 'Error importing form file', severity: 'error' });
      }
    };
    reader.readAsText(file);

    // Clear the input value to allow importing the same file again
    event.target.value = '';
  };

  const handleUpdateElement = (updatedElement) => {
    setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
    setSelectedElement(updatedElement);
  };

  // Add a dedicated settings handler
  const handleOpenSettings = () => {
    setSettingsDialogOpen(true);
    setShareDialogOpen(true); // Ensure at least one is open
  };

  // Enhanced undo/redo functionality
  const pushToUndoStack = useCallback((currentState) => {
    const newUndoStack = [...undoStack, currentState].slice(-20); // Keep last 20 states
    // Update the undo stack through the hook
    // This should be handled in the useFormBuilder hook
  }, [undoStack]);

  // Save state before making changes for undo functionality
  const saveStateForUndo = useCallback(() => {
    const currentState = {
      formElements: JSON.parse(JSON.stringify(formElements)),
      formSettings: JSON.parse(JSON.stringify(formSettings)),
      timestamp: Date.now(),
    };
    pushToUndoStack(currentState);
  }, [formElements, formSettings, pushToUndoStack]);

  // Enhanced element handlers that save state for undo
  const handleDeleteElementWithUndo = useCallback((elementId) => {
    saveStateForUndo();
    setFormElements((prev) => prev.filter((el) => el.id !== elementId));
    if (selectedElement && selectedElement.id === elementId) {
      setSelectedElement(null);
    }
  }, [saveStateForUndo, selectedElement]);

  const handleAddElementWithUndo = useCallback((element) => {
    saveStateForUndo();
    const newElement = {
      ...element,
      id: `${element.type}_${Date.now()}`,
    };
    setFormElements((prev) => [...prev, newElement]);
  }, [saveStateForUndo]);

  const handleUpdateElementWithUndo = useCallback((updatedElement) => {
    saveStateForUndo();
    setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
    setSelectedElement(updatedElement);
  }, [saveStateForUndo, selectedElement]);

  // Enhanced move function with undo support
  const handleMoveElementWithUndo = useCallback((dragIndex, hoverIndex) => {
    saveStateForUndo();
    moveFormElement(dragIndex, hoverIndex);
  }, [saveStateForUndo, moveFormElement]);

  const aiFormSnapshot = useMemo(() => ({
    formElements,
    formSettings,
    formName,
    formDescription,
  }), [formElements, formSettings, formName, formDescription]);

  const applyAiFormSnapshot = useCallback((proposal) => {
    if (!proposal || typeof proposal !== 'object') return;
    saveStateForUndo();
    if (Array.isArray(proposal.formElements)) {
      setFormElements(proposal.formElements);
      setSelectedElement(null);
    }
    if (proposal.formSettings && typeof proposal.formSettings === 'object') {
      setFormSettings((prev) => ({ ...prev, ...proposal.formSettings }));
    }
    if (typeof proposal.name === 'string' && proposal.name.trim()) {
      setFormName(proposal.name.trim());
    }
    if (proposal.description !== undefined) {
      const d = proposal.description;
      setFormDescription(d == null ? '' : String(d));
    }
    setSnackbar({ open: true, message: 'Applied AI form proposal.', severity: 'success' });
  }, [saveStateForUndo]);

  // Sync formName and formDescription with formSettings on mount and when formSettings changes
  useEffect(() => {
    setFormName(formSettings.title || '');
    setFormDescription(formSettings.description || '');
  }, [formSettings]);

  return (
    <StyledEngineProvider injectFirst>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: embedded ? '100%' : '100vh',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
      >
        <FormBuilderHeader
          formName={formName}
          setFormName={setFormName}
          formElements={formElements}
          isAutoSaving={isAutoSaving}
          undoStack={undoStack}
          redoStack={redoStack}
          previewMode={previewMode}
          setPreviewMode={setPreviewMode}
          publishing={publishing}
          darkMode={darkMode}
          showPropertiesPanel={showPropertiesPanel}
          setShowPropertiesPanel={setShowPropertiesPanel}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onLoad={() => setLoadDialogOpen(true)}
          onSave={() => setSaveDialogOpen(true)}
          onPublish={publishForm}
          onShare={handleOpenSettings}
          onSettings={handleOpenSettings}
          onFormSettings={handleOpenSettings}
          onExport={exportFormAsJSON}
          onImport={importFormFromJSON}
          onToggleTheme={handleToggleTheme}
          // Add save state function for child components
          onSaveStateForUndo={saveStateForUndo}
        />

        <DndProvider backend={HTML5Backend}>
          <Box sx={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            flexDirection: { xs: 'column', md: 'row' },
          }}
          >
            {/* Enhanced Widget Sidebar */}
            {!isMobile ? (
              <Paper
                elevation={2}
                sx={{
                  width: 320,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 0,
                  backgroundColor: 'background.paper',
                  borderRight: 1,
                  borderColor: 'divider',
                  height: '100%',
                }}
              >
                <Box sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                >
                  <EnhancedWidgetList
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    selectedTab={selectedTab}
                    onElementSelect={handleAddElementWithUndo} // Use undo-aware version
                  />
                </Box>
              </Paper>
            ) : (
              <Drawer
                anchor="bottom"
                open={drawerOpen}
                onClose={toggleDrawer}
                ModalProps={{ keepMounted: true }}
                sx={{
                  '& .MuiDrawer-paper': {
                    height: '60vh',
                    bgcolor: 'background.paper',
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                  },
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Add Components
                    </Typography>
                    <IconButton onClick={toggleDrawer} size="small">
                      <ClearIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{
                    height: 'calc(60vh - 80px)',
                    overflowY: 'auto',
                  }}
                  >
                    <EnhancedWidgetList />
                  </Box>
                </Box>
              </Drawer>
            )}

            {/* Main Content Area */}
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              position: 'relative',
            }}
            >
              {/* Mobile Add Button */}
              {isMobile && (
                <Fab
                  color="primary"
                  onClick={toggleDrawer}
                  sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    zIndex: 1000,
                  }}
                >
                  <AddIcon />
                </Fab>
              )}

              {/* Content Tabs */}
              <Paper
                elevation={0}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                }}
                >
                  <Tabs
                    value={selectedTab}
                    onChange={(e, newValue) => setSelectedTab(newValue)}
                    textColor="primary"
                    indicatorColor="primary"
                  >
                    <Tab
                      icon={<EditIcon fontSize="small" />}
                      iconPosition="start"
                      label="Design"
                    />
                    <Tab
                      icon={<PreviewIcon fontSize="small" />}
                      iconPosition="start"
                      label="Preview"
                    />
                    <Tab
                      icon={<CodeIcon fontSize="small" />}
                      iconPosition="start"
                      label="Code"
                    />
                    <Tab
                      icon={<CodeIcon fontSize="small" />}
                      iconPosition="start"
                      label="Contract"
                    />
                    <Tab
                      icon={<AutoAwesomeIcon fontSize="small" />}
                      iconPosition="start"
                      label="AI"
                    />
                  </Tabs>

                  {/* ADD FORM SETTINGS BUTTON */}
                  <Tooltip title="Form Settings">
                    <IconButton
                      onClick={handleOpenSettings}
                      size="small"
                      sx={{
                        ml: 2,
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.light', color: 'white' },
                      }}
                    >
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>

              {/* SINGLE Properties Panel - ADD GRID FIELD SUPPORT */}
              {showPropertiesPanel && selectedElement && (
                <Paper
                  elevation={2}
                  sx={{
                    position: 'fixed',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 380,
                    maxHeight: '80vh',
                    overflow: 'auto',
                    zIndex: 1200,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Properties Panel
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowPropertiesPanel(false)}
                      sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {selectedElement.type}
                      {' '}
                      -
                      {selectedElement.label}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={2}>
                      {/* Common Properties */}
                      <TextField
                        label="Label"
                        value={selectedElement.label || ''}
                        onChange={(e) => {
                          const updatedElement = { ...selectedElement, label: e.target.value };
                          setSelectedElement(updatedElement);
                          setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                        }}
                        size="small"
                        fullWidth
                      />

                      <TextField
                        label="ID"
                        value={selectedElement.id || ''}
                        onChange={(e) => {
                          const updatedElement = { ...selectedElement, id: e.target.value };
                          setSelectedElement(updatedElement);
                          setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                        }}
                        size="small"
                        fullWidth
                        helperText="Unique identifier for this field"
                      />

                      <FormControlLabel
                        control={(
                          <Switch
                            checked={selectedElement.required || false}
                            onChange={(e) => {
                              const updatedElement = { ...selectedElement, required: e.target.checked };
                              setSelectedElement(updatedElement);
                              setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                            }}
                          />
                        )}
                        label="Required"
                      />

                      <FormControlLabel
                        control={(
                          <Switch
                            checked={selectedElement.disabled || false}
                            onChange={(e) => {
                              const updatedElement = { ...selectedElement, disabled: e.target.checked };
                              setSelectedElement(updatedElement);
                              setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                            }}
                          />
                        )}
                        label="Disabled"
                      />

                      {/* Grid-specific Properties */}
                      {selectedElement.type === 'grid' && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" color="primary">Grid Properties</Typography>

                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>Columns:</Typography>
                            {(selectedElement.columns || []).map((column, index) => (
                              <Card key={index} variant="outlined" sx={{ mb: 1, p: 1 }}>
                                <Stack spacing={1}>
                                  <TextField
                                    label="Column Label"
                                    value={column.label || ''}
                                    onChange={(e) => {
                                      const updatedColumns = [...(selectedElement.columns || [])];
                                      updatedColumns[index] = { ...column, label: e.target.value };
                                      const updatedElement = { ...selectedElement, columns: updatedColumns };
                                      setSelectedElement(updatedElement);
                                      setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                    }}
                                    size="small"
                                    fullWidth
                                  />
                                  <TextField
                                    label="Column Key"
                                    value={column.key || ''}
                                    onChange={(e) => {
                                      const updatedColumns = [...(selectedElement.columns || [])];
                                      updatedColumns[index] = { ...column, key: e.target.value };
                                      const updatedElement = { ...selectedElement, columns: updatedColumns };
                                      setSelectedElement(updatedElement);
                                      setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                    }}
                                    size="small"
                                    fullWidth
                                  />
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Column Type</InputLabel>
                                    <Select
                                      value={column.type || 'string'}
                                      onChange={(e) => {
                                        const updatedColumns = [...(selectedElement.columns || [])];
                                        updatedColumns[index] = { ...column, type: e.target.value };
                                        const updatedElement = { ...selectedElement, columns: updatedColumns };
                                        setSelectedElement(updatedElement);
                                        setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                      }}
                                      label="Column Type"
                                    >
                                      <MenuItem value="string">Text</MenuItem>
                                      <MenuItem value="number">Number</MenuItem>
                                      <MenuItem value="boolean">Boolean</MenuItem>
                                      <MenuItem value="date">Date</MenuItem>
                                    </Select>
                                  </FormControl>
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      const updatedColumns = (selectedElement.columns || []).filter((_, i) => i !== index);
                                      const updatedElement = { ...selectedElement, columns: updatedColumns };
                                      setSelectedElement(updatedElement);
                                      setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                    }}
                                  >
                                    Remove Column
                                  </Button>
                                </Stack>
                              </Card>
                            ))}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                const newColumn = {
                                  id: `col_${Date.now()}`,
                                  key: `column_${(selectedElement.columns || []).length + 1}`,
                                  label: `Column ${(selectedElement.columns || []).length + 1}`,
                                  type: 'string',
                                };
                                const updatedColumns = [...(selectedElement.columns || []), newColumn];
                                const updatedElement = { ...selectedElement, columns: updatedColumns };
                                setSelectedElement(updatedElement);
                                setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                              }}
                            >
                              Add Column
                            </Button>
                          </Box>
                        </>
                      )}

                      {/* Type-specific Properties - RESTORED */}
                      {(selectedElement.type === 'text' || selectedElement.type === 'email' || selectedElement.type === 'password' || selectedElement.type === 'url') && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" color="primary">Text Input Properties</Typography>

                          <TextField
                            label="Placeholder"
                            value={selectedElement.placeholder || ''}
                            onChange={(e) => {
                              const updatedElement = { ...selectedElement, placeholder: e.target.value };
                              setSelectedElement(updatedElement);
                              setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                            }}
                            size="small"
                            fullWidth
                          />

                          <TextField
                            label="Default Value"
                            value={selectedElement.value || ''}
                            onChange={(e) => {
                              const updatedElement = { ...selectedElement, value: e.target.value };
                              setSelectedElement(updatedElement);
                              setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                            }}
                            size="small"
                            fullWidth
                          />

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                label="Min Length"
                                type="number"
                                value={selectedElement.minLength || ''}
                                onChange={(e) => {
                                  const updatedElement = { ...selectedElement, minLength: parseInt(e.target.value) || undefined };
                                  setSelectedElement(updatedElement);
                                  setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Max Length"
                                type="number"
                                value={selectedElement.maxLength || ''}
                                onChange={(e) => {
                                  const updatedElement = { ...selectedElement, maxLength: parseInt(e.target.value) || undefined };
                                  setSelectedElement(updatedElement);
                                  setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                          </Grid>

                          <TextField
                            label="Pattern (Regex)"
                            value={selectedElement.pattern || ''}
                            onChange={(e) => {
                              const updatedElement = { ...selectedElement, pattern: e.target.value };
                              setSelectedElement(updatedElement);
                              setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                            }}
                            size="small"
                            fullWidth
                            helperText="Regular expression for validation"
                          />
                        </>
                      )}

                      {selectedElement.type === 'textarea' && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" color="primary">Text Area Properties</Typography>

                          <TextField
                            label="Placeholder"
                            value={selectedElement.placeholder || ''}
                            onChange={(e) => {
                              const updatedElement = { ...selectedElement, placeholder: e.target.value };
                              setSelectedElement(updatedElement);
                              setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                            }}
                            size="small"
                            fullWidth
                          />

                          <TextField
                            label="Rows"
                            type="number"
                            value={selectedElement.rows || 4}
                            onChange={(e) => {
                              const updatedElement = { ...selectedElement, rows: parseInt(e.target.value) || 4 };
                              setSelectedElement(updatedElement);
                              setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                            }}
                            size="small"
                            fullWidth
                            inputProps={{ min: 2, max: 20 }}
                          />

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                label="Min Length"
                                type="number"
                                value={selectedElement.minLength || ''}
                                onChange={(e) => {
                                  const updatedElement = { ...selectedElement, minLength: parseInt(e.target.value) || undefined };
                                  setSelectedElement(updatedElement);
                                  setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Max Length"
                                type="number"
                                value={selectedElement.maxLength || ''}
                                onChange={(e) => {
                                  const updatedElement = { ...selectedElement, maxLength: parseInt(e.target.value) || undefined };
                                  setSelectedElement(updatedElement);
                                  setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        </>
                      )}

                      {selectedElement.type === 'number' && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" color="primary">Number Properties</Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                label="Minimum"
                                type="number"
                                value={selectedElement.minimum || selectedElement.min || ''}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  const updatedElement = {
                                    ...selectedElement,
                                    minimum: isNaN(value) ? undefined : value,
                                    min: isNaN(value) ? undefined : value,
                                  };
                                  setSelectedElement(updatedElement);
                                  setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Maximum"
                                type="number"
                                value={selectedElement.maximum || selectedElement.max || ''}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  const updatedElement = {
                                    ...selectedElement,
                                    maximum: isNaN(value) ? undefined : value,
                                    max: isNaN(value) ? undefined : value,
                                  };
                                  setSelectedElement(updatedElement);
                                  setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                          </Grid>

                          <TextField
                            label="Step"
                            type="number"
                            value={selectedElement.step || ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              const updatedElement = { ...selectedElement, step: isNaN(value) ? undefined : value };
                              setSelectedElement(updatedElement);
                              setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                            }}
                            size="small"
                            fullWidth
                            helperText="Increment step for number input"
                          />

                          <TextField
                            label="Default Value"
                            type="number"
                            value={selectedElement.value || selectedElement.defaultValue || ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              const updatedElement = {
                                ...selectedElement,
                                value: isNaN(value) ? undefined : value,
                                defaultValue: isNaN(value) ? undefined : value,
                              };
                              setSelectedElement(updatedElement);
                              setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                            }}
                            size="small"
                            fullWidth
                          />
                        </>
                      )}

                      {(selectedElement.type === 'combobox' || selectedElement.type === 'listbox' || selectedElement.type === 'radiobuttons') && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" color="primary">Selection Properties</Typography>

                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>Options (one per line):</Typography>
                            <TextField
                              multiline
                              rows={4}
                              value={(selectedElement.options || []).join('\n')}
                              onChange={(e) => {
                                const options = e.target.value.split('\n').filter((opt) => opt.trim());
                                const updatedElement = { ...selectedElement, options };
                                setSelectedElement(updatedElement);
                                setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                              }}
                              size="small"
                              fullWidth
                              placeholder="Option 1&#10;Option 2&#10;Option 3"
                            />
                          </Box>

                          {selectedElement.type === 'listbox' && (
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={selectedElement.multiple || false}
                                  onChange={(e) => {
                                    const updatedElement = { ...selectedElement, multiple: e.target.checked };
                                    setSelectedElement(updatedElement);
                                    setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                  }}
                                />
                              )}
                              label="Allow Multiple Selections"
                            />
                          )}
                        </>
                      )}

                      {selectedElement.type === 'checkbox' && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" color="primary">Checkbox Properties</Typography>

                          <FormControlLabel
                            control={(
                              <Switch
                                checked={selectedElement.defaultValue || selectedElement.value || false}
                                onChange={(e) => {
                                  const updatedElement = {
                                    ...selectedElement,
                                    defaultValue: e.target.checked,
                                    value: e.target.checked,
                                  };
                                  setSelectedElement(updatedElement);
                                  setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                                }}
                              />
                            )}
                            label="Checked by Default"
                          />
                        </>
                      )}

                      {/* Styling Properties */}
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="primary">Styling</Typography>

                      <TextField
                        label="CSS Classes"
                        value={selectedElement.className || ''}
                        onChange={(e) => {
                          const updatedElement = { ...selectedElement, className: e.target.value };
                          setSelectedElement(updatedElement);
                          setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                        }}
                        size="small"
                        fullWidth
                        helperText="Space-separated CSS class names"
                      />

                      <TextField
                        label="Help Text"
                        value={selectedElement.helperText || selectedElement.helpText || ''}
                        onChange={(e) => {
                          const updatedElement = {
                            ...selectedElement,
                            helperText: e.target.value,
                            helpText: e.target.value,
                          };
                          setSelectedElement(updatedElement);
                          setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                        }}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        helperText="Additional help text for users"
                      />

                      {/* Conditional Logic */}
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="primary">Conditional Logic</Typography>

                      <TextField
                        label="Show When"
                        value={selectedElement.showWhen || ''}
                        onChange={(e) => {
                          const updatedElement = { ...selectedElement, showWhen: e.target.value };
                          setSelectedElement(updatedElement);
                          setFormElements((prev) => prev.map((el) => (el.id === selectedElement.id ? updatedElement : el)));
                        }}
                        size="small"
                        fullWidth
                        placeholder="field_id = 'value'"
                        helperText="Condition for showing this field"
                      />
                    </Stack>
                  </Box>
                </Paper>
              )}

              {/* Main Content - FIXED SCROLLING */}
              <Box sx={{
                flex: 1,
                minHeight: 0,
                position: 'relative',
              }}
              >
                {selectedTab === 0 && (
                  <Box sx={{
                    height: '100%',
                    overflow: 'auto',
                    p: { xs: 1, sm: 2 },
                    bgcolor: 'grey.50',
                  }}
                  >
                    <FormEditor
                      formElements={formElements}
                      setFormElements={setFormElements}
                      setJsonCode={setJsonCode}
                      handleOpenModal={handleOpenModal}
                      moveFormElement={handleMoveElementWithUndo} // Use undo-aware version
                      onElementSelect={setSelectedElement}
                      selectedElement={selectedElement}
                      onSaveStateForUndo={saveStateForUndo} // Pass down for undo support
                    />
                  </Box>
                )}

                {selectedTab === 1 && (
                  <Box sx={{
                    height: '100%',
                    overflow: 'auto',
                    p: { xs: 1, sm: 2 },
                    bgcolor: 'background.paper',
                  }}
                  >
                    <Box sx={{
                      maxWidth: previewMode === 'mobile' ? 375 : previewMode === 'tablet' ? 768 : '100%',
                      mx: 'auto',
                      transition: 'max-width 0.3s ease',
                      pb: 4,
                    }}
                    >
                      <FormRuntime
                        formElements={formElements}
                        initialData={{}}
                        formSettings={formSettings}
                      />
                    </Box>
                  </Box>
                )}

                {selectedTab === 2 && (
                  <Box sx={{
                    height: '100%',
                    overflow: 'auto',
                    p: { xs: 1, sm: 2 },
                    bgcolor: 'background.paper',
                  }}
                  >
                    <JSONEditor
                      jsonCode={jsonCode}
                      setJsonCode={setJsonCode}
                    />
                  </Box>
                )}

                {selectedTab === 3 && (
                  <Box sx={{
                    height: '100%',
                    overflow: 'auto',
                    p: { xs: 1, sm: 2 },
                    bgcolor: 'background.paper',
                  }}
                  >
                    <Stack spacing={2}>
                      <Alert severity={formContractDiagnostics.valid ? 'success' : 'warning'}>
                        {formContractDiagnostics.valid
                          ? 'Form payload contract is valid.'
                          : formContractDiagnostics.errors.join(' ')}
                        {formContractDiagnostics.warnings.length > 0 && ` ${formContractDiagnostics.warnings.slice(0, 3).join(' ')}`}
                      </Alert>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                          Versioned payload contract
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={12}
                          value={JSON.stringify(formPayloadContract, null, 2)}
                          InputProps={{
                            readOnly: true,
                            sx: { fontFamily: 'monospace', fontSize: 12 },
                          }}
                        />
                      </Paper>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Debug payload for Studio flow input
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<CopyIcon />}
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(JSON.stringify(formDebugPayload, null, 2));
                                setSnackbar({ open: true, message: 'Copied debug payload — paste into Flow Debug input.', severity: 'success' });
                              } catch {
                                setSnackbar({ open: true, message: 'Copy failed', severity: 'error' });
                              }
                            }}
                          >
                            Copy for Flow Debug
                          </Button>
                        </Stack>
                        <TextField
                          fullWidth
                          multiline
                          minRows={6}
                          value={JSON.stringify(formDebugPayload, null, 2)}
                          InputProps={{
                            readOnly: true,
                            sx: { fontFamily: 'monospace', fontSize: 12 },
                          }}
                        />
                      </Paper>
                    </Stack>
                  </Box>
                )}

                {selectedTab === 4 && (
                  <Box sx={{
                    height: '100%',
                    overflow: 'auto',
                    p: { xs: 1, sm: 2 },
                    bgcolor: 'background.paper',
                  }}
                  >
                    <FormBuilderAiChatPanel
                      snapshot={aiFormSnapshot}
                      formDesignId={loadedFormDesignId}
                      onApplySnapshot={applyAiFormSnapshot}
                      useV2AiEndpoint={embedded}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DndProvider>

        {/* Modal editor */}
        {isModalOpen && (
          <ModalFormEditor
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            widget={selectedElement}
            onSave={handleSaveChanges}
          />
        )}

        {/* Enhanced Save Dialog */}
        <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SaveIcon color="primary" />
              Save Form Design
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="Form Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                fullWidth
                required
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EditIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Describe the purpose and functionality of this form..."
              />

              {/* Form Statistics */}
              <Card variant="outlined">
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Form Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {formElements.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Fields
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {formElements.filter((el) => el.required).length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Required
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {new Set(formElements.map((el) => el.type)).size}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Types
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={saveFormDesign}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!formName.trim()}
            >
              Save Design
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Load Dialog */}
        <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LoadIcon color="primary" />
                Load Form Design
              </Box>
              <Chip
                label={`${savedForms.length} forms`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <TextField
                placeholder="Search forms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {savedForms.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LoadIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No saved form designs found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create and save your first form design to see it here
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {savedForms
                  .filter((form) => form.name.toLowerCase().includes(searchQuery.toLowerCase())
                    || (form.description && form.description.toLowerCase().includes(searchQuery.toLowerCase())))
                  .map((form) => (
                    <ListItem
                      key={form._id}
                      divider
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ListItemText
                        primary={(
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, mb: 0.5,
                          }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                              {form.name}
                            </Typography>
                            {form.status && (
                            <Chip
                              label={form.status}
                              size="small"
                              color={form.status === 'published' ? 'success' : 'default'}
                              variant="outlined"
                            />
                            )}
                          </Box>
                      )}
                        secondary={(
                          <Stack spacing={1}>
                            {form.description && (
                            <Typography variant="body2" color="text.secondary">
                              {form.description}
                            </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                Created:
                                {' '}
                                {new Date(form.createdAt).toLocaleDateString()}
                              </Typography>
                              {form.updatedAt && (
                              <Typography variant="caption" color="text.secondary">
                                Updated:
                                {' '}
                                {new Date(form.updatedAt).toLocaleDateString()}
                              </Typography>
                              )}
                              <Chip
                                label={`${(form.formElements || []).length} fields`}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </Stack>
                      )}
                      />
                      <ListItemSecondaryAction>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Load Form">
                            <Button
                              size="small"
                              onClick={() => loadFormDesign(form._id)}
                              variant="contained"
                              startIcon={<LoadIcon />}
                            >
                              Load
                            </Button>
                          </Tooltip>
                          <Tooltip title="Duplicate">
                            <IconButton
                              size="small"
                              onClick={() => duplicateForm(form._id)}
                            >
                              <DuplicateIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteFormDesign(form._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setLoadDialogOpen(false)}>Close</Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setLoadDialogOpen(false);
                setSaveDialogOpen(true);
              }}
            >
              Create New
            </Button>
          </DialogActions>
        </Dialog>

        {/* Form Settings Dialog - Make sure this is connected properly */}
        <Dialog
          open={shareDialogOpen || settingsDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setSettingsDialogOpen(false);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon color="primary" />
              Form Settings
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Form Title"
                    value={formSettings.title}
                    onChange={(e) => setFormSettings((prev) => ({ ...prev, title: e.target.value }))}
                    fullWidth
                    placeholder="Enter form title..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Theme</InputLabel>
                    <Select
                      value={formSettings.theme}
                      onChange={(e) => setFormSettings((prev) => ({ ...prev, theme: e.target.value }))}
                      label="Theme"
                    >
                      <MenuItem value="default">Default</MenuItem>
                      <MenuItem value="modern">Modern</MenuItem>
                      <MenuItem value="classic">Classic</MenuItem>
                      <MenuItem value="minimal">Minimal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Form Description"
                    value={formSettings.description}
                    onChange={(e) => setFormSettings((prev) => ({ ...prev, description: e.target.value }))}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Describe your form..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Submit Button Text"
                    value={formSettings.submitText}
                    onChange={(e) => setFormSettings((prev) => ({ ...prev, submitText: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Reset Button Text"
                    value={formSettings.resetText}
                    onChange={(e) => setFormSettings((prev) => ({ ...prev, resetText: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Form Width</InputLabel>
                    <Select
                      value={formSettings.formWidth || 'medium'}
                      onChange={(e) => setFormSettings((prev) => ({ ...prev, formWidth: e.target.value }))}
                      label="Form Width"
                    >
                      <MenuItem value="small">Small (600px)</MenuItem>
                      <MenuItem value="medium">Medium (800px)</MenuItem>
                      <MenuItem value="large">Large (1000px)</MenuItem>
                      <MenuItem value="full">Full Width</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Button Style</InputLabel>
                    <Select
                      value={formSettings.buttonStyle || 'contained'}
                      onChange={(e) => setFormSettings((prev) => ({ ...prev, buttonStyle: e.target.value }))}
                      label="Button Style"
                    >
                      <MenuItem value="contained">Filled</MenuItem>
                      <MenuItem value="outlined">Outlined</MenuItem>
                      <MenuItem value="text">Text</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider />

              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Form Options
              </Typography>

              <Stack spacing={2}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={formSettings.showProgress}
                      onChange={(e) => setFormSettings((prev) => ({ ...prev, showProgress: e.target.checked }))}
                    />
                  )}
                  label="Show Progress Bar"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={formSettings.allowDrafts}
                      onChange={(e) => setFormSettings((prev) => ({ ...prev, allowDrafts: e.target.checked }))}
                    />
                  )}
                  label="Allow Saving Drafts"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={formSettings.emailNotifications}
                      onChange={(e) => setFormSettings((prev) => ({ ...prev, emailNotifications: e.target.checked }))}
                    />
                  )}
                  label="Email Notifications"
                />
              </Stack>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Form Preview
                  </Typography>
                  <Typography variant="body2">
                    <strong>Title:</strong>
                    {' '}
                    {formSettings.title || 'Untitled Form'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Theme:</strong>
                    {' '}
                    {formSettings.theme}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Fields:</strong>
                    {' '}
                    {formElements.length}
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => {
              setShareDialogOpen(false);
              setSettingsDialogOpen(false);
            }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShareDialogOpen(false);
                setSettingsDialogOpen(false);
                setSnackbar({ open: true, message: 'Form settings saved!', severity: 'success' });
                if (selectedTab !== 1) {
                  setSelectedTab(1);
                }
              }}
              variant="contained"
              startIcon={<SaveIcon />}
            >
              Save & Preview
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </StyledEngineProvider>
  );
}

export default FormBuilder;
